/**
 * ProviderRegistry - Authentication Provider Management
 *
 * Manages the registry of available authentication adapters and
 * provides a way to select the active provider at runtime.
 */

import type {
  AuthAdapter,
  AuthProviderRegistry,
  AuthProviderType,
} from './AuthAdapter';

/**
 * Implementation of the AuthProviderRegistry
 * Manages authentication adapter registration and selection
 */
class ProviderRegistryImpl implements AuthProviderRegistry {
  private adapters: Map<AuthProviderType, AuthAdapter> = new Map();
  private activeProviderType: AuthProviderType | null = null;

  /**
   * Register an authentication adapter
   * @param adapter - The adapter to register
   */
  register(adapter: AuthAdapter): void {
    this.adapters.set(adapter.providerType, adapter);

    // Auto-set as active if it's the first registered adapter
    if (this.activeProviderType === null) {
      this.activeProviderType = adapter.providerType;
    }
  }

  /**
   * Get a specific adapter by provider type
   * @param providerType - The provider type to retrieve
   */
  get(providerType: AuthProviderType): AuthAdapter | undefined {
    return this.adapters.get(providerType);
  }

  /**
   * Get all registered adapters
   */
  getAll(): AuthAdapter[] {
    return Array.from(this.adapters.values());
  }

  /**
   * Set the active authentication provider
   * @param providerType - The provider type to activate
   * @throws Error if the provider is not registered
   */
  setActive(providerType: AuthProviderType): void {
    if (!this.adapters.has(providerType)) {
      throw new Error(
        `Authentication provider '${providerType}' is not registered. ` +
          `Available providers: ${Array.from(this.adapters.keys()).join(', ')}`
      );
    }
    this.activeProviderType = providerType;
  }

  /**
   * Get the currently active authentication adapter
   */
  getActive(): AuthAdapter | undefined {
    if (this.activeProviderType === null) {
      return undefined;
    }
    return this.adapters.get(this.activeProviderType);
  }

  /**
   * Check if any providers are registered
   */
  hasProviders(): boolean {
    return this.adapters.size > 0;
  }

  /**
   * Get the active provider type
   */
  getActiveProviderType(): AuthProviderType | null {
    return this.activeProviderType;
  }
}

// Singleton instance of the provider registry
export const providerRegistry = new ProviderRegistryImpl();

/**
 * Helper function to get the active authentication adapter
 * @throws Error if no active provider is configured
 */
export function getActiveAuthAdapter(): AuthAdapter {
  const adapter = providerRegistry.getActive();
  if (!adapter) {
    throw new Error(
      'No active authentication provider configured. ' +
        'Please ensure an auth provider is registered and set as active.'
    );
  }
  return adapter;
}
